import type { WaveData, WindData } from '../types';

import { hasMarineData } from './marineData';

export type SeaStateLevel = 'unavailable' | 'smooth' | 'slight-chop' | 'moderate-chop' | 'choppy' | 'heavy-chop';

export interface SeaState {
  level: SeaStateLevel;
  label: string;
  color: string;
  backgroundColor: string;
}

interface SeaStateSample {
  waves: WaveData;
  wind: WindData;
}

const SEA_STATES: SeaState[] = [
  { level: 'smooth', label: 'Smooth Seas', color: '#0066CC', backgroundColor: '#E3F2FD' },
  { level: 'slight-chop', label: 'Slight Chop', color: '#0066CC', backgroundColor: '#D1E8FF' },
  { level: 'moderate-chop', label: 'Moderate Chop', color: '#0057B8', backgroundColor: '#B8DBF7' },
  { level: 'choppy', label: 'Choppy', color: '#004C99', backgroundColor: '#90C4F2' },
  { level: 'heavy-chop', label: 'Heavy Chop', color: '#003C78', backgroundColor: '#5FA8E8' },
];

const UNAVAILABLE_SEA_STATE: SeaState = {
  level: 'unavailable',
  label: 'Marine unavailable',
  color: '#5F6368',
  backgroundColor: '#E5E7EB',
};

/**
 * Converts forecast wind and locally generated wind waves into a quick,
 * boat-friendly chop label. A long-period swell is intentionally excluded:
 * it can raise total seas without making the surface choppy.
 */
const getSeaStateScore = (waves: WaveData, wind: WindData): number => {
  const totalHeight = waves.height;
  const height = waves.windWaveHeight
    ?? (waves.swellHeight !== undefined
      ? Math.sqrt(Math.max(0, totalHeight ** 2 - waves.swellHeight ** 2))
      : totalHeight);
  const period = waves.windWavePeriod ?? waves.period;
  const windSpeed = wind.speed;

  let waveScore = 0;
  if (height > 5) waveScore = 4;
  else if (height > 3.5) waveScore = 3;
  else if (height > 2) waveScore = 2;
  else if (height > 1) waveScore = 1;

  let windScore = 0;
  if (windSpeed > 25) windScore = 4;
  else if (windSpeed > 18) windScore = 3;
  else if (windSpeed > 13) windScore = 2;
  else if (windSpeed > 8) windScore = 1;

  // Short-period wind waves feel steeper than their height alone suggests.
  let shortPeriodScore = 0;
  if (height >= 2 && period <= 4.5) shortPeriodScore = 3;
  else if (height >= 1.5 && period <= 5.5) shortPeriodScore = 2;
  else if (height >= 1 && period <= 6) shortPeriodScore = 1;

  return Math.max(waveScore, windScore, shortPeriodScore);
};

export const getSeaState = (waves: WaveData, wind: WindData): SeaState =>
  hasMarineData(waves) ? SEA_STATES[getSeaStateScore(waves, wind)] : UNAVAILABLE_SEA_STATE;

/** Average the hourly chop score for a day rather than letting one rough hour label the entire day. */
export const getAverageSeaState = (samples: SeaStateSample[]): SeaState => {
  const availableSamples = samples.filter(sample => hasMarineData(sample.waves));
  if (!availableSamples.length) return UNAVAILABLE_SEA_STATE;
  const averageScore = availableSamples.reduce((total, sample) => total + getSeaStateScore(sample.waves, sample.wind), 0) / availableSamples.length;
  return SEA_STATES[Math.round(averageScore)];
};
